/**
 * Per-route <head> manager.
 *
 * This is a client-rendered SPA served from a single index.html, so every
 * route would otherwise inherit the homepage's title, description AND its
 * canonical link — which tells crawlers that /products, /about and /contact
 * are all duplicates of /. Each page mounts <Seo> to correct that.
 *
 * Deliberately dependency-free (no react-helmet): it only ever touches a
 * handful of tags it owns, marked with data-seo so a route change can clear
 * the previous route's tags without disturbing the static ones in
 * public/index.html.
 */

import { useEffect } from "react";

export const SITE_URL = "https://devanshicorporation.com";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/bags-warehouse.jpg`;

const OWNED = "data-seo";

/** Reuse the tag the previous route left behind, or create one. */
const tag = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    el.setAttribute(OWNED, "");
    document.head.appendChild(el);
  }
  return el;
};

const setMeta = (attr, key, content) => {
  if (!content) return;
  const el = tag(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute(attr, key);
    return m;
  });
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  const el = tag(`link[rel="${rel}"]`, () => {
    const l = document.createElement("link");
    l.setAttribute("rel", rel);
    return l;
  });
  el.setAttribute("href", href);
};

/**
 * Route-scoped structured data. Keyed by id so a route can replace its own
 * block, and removed on unmount so stale schema never leaks to the next page.
 */
const setJsonLd = (id, data) => {
  const selector = `script[type="application/ld+json"][data-seo-id="${id}"]`;
  let el = document.head.querySelector(selector);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const Seo = ({
  title,
  description,
  /** Canonical path, leading slash, no origin. */
  path = "/",
  image = DEFAULT_OG_IMAGE,
  imageAlt = "Devanshi Corporation warehouse stacked with industrial chemical bags",
  robots = "index, follow",
  /** [id, schemaObject] pairs scoped to this route. */
  schemas = [],
}) => {
  const schemaKey = JSON.stringify(schemas);

  useEffect(() => {
    const url = `${SITE_URL}${path}`;

    if (title) document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", robots);
    setLink("canonical", url);

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image);
    setMeta("property", "og:image:alt", imageAlt);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
    setMeta("name", "twitter:image:alt", imageAlt);

    for (const [id, data] of schemas) setJsonLd(id, data);

    return () => {
      for (const [id] of schemas) setJsonLd(id, null);
    };
    // schemaKey stands in for the schemas array identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, imageAlt, robots, schemaKey]);

  return null;
};

export default Seo;
